import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine, ComposedChart, Line,
} from 'recharts';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

import { RangeSelector, useTimeRange } from '../data/TimeRange';

// Warm "solar" chart palette — gold is the primary series, green the overlay,
// no blue/purple (off-brand).
const COLORS = {
  steps: '#FFB800',
  distance: '#10B981',
  calories: '#E09E00',
  activeMinutes: '#F0791A',
  movingAvg: '#10B981',
};

const ActivityChart = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { inRange, spanDays, selectorProps } = useTimeRange();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.get('/api/fitbit/activity');
        if (response.status === 200) setAllData(response.data);
      } catch (error) {
        toast.error('Failed to load activity data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    if (!allData.length) return [];
    return allData.filter(d => inRange(d.date));
  }, [allData, inRange]);

  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    const latest = filteredData[filteredData.length - 1];
    const avg = (field) => {
      const vals = filteredData.filter(d => d[field] != null).map(d => d[field]);
      return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
    };
    const avgFloat = (field) => {
      const vals = filteredData.filter(d => d[field] != null).map(d => d[field]);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    return {
      latest,
      avgSteps: avg('steps'),
      avgDistance: avgFloat('distance'),
      avgCalories: avg('calories'),
      avgActiveMinutes: avg('minutes_very_active'),
      totalDays: filteredData.length,
    };
  }, [filteredData]);

  const { chartData, aggregation } = useMemo(() => {
    if (!filteredData.length) return { chartData: [], aggregation: 'daily' };

    let agg = 'daily';
    if (filteredData.length > 365) agg = 'monthly';
    else if (filteredData.length > 90) agg = 'weekly';

    let processed = filteredData;
    if (agg !== 'daily') {
      const groups = {};
      filteredData.forEach(d => {
        const dt = new Date(d.date + 'T00:00:00');
        let key;
        if (agg === 'weekly') {
          const start = new Date(dt);
          start.setDate(dt.getDate() - dt.getDay());
          key = start.toISOString().split('T')[0];
        } else {
          key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`;
        }
        if (!groups[key]) groups[key] = [];
        groups[key].push(d);
      });

      processed = Object.entries(groups)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([periodStart, items]) => {
          const avg = (field) => {
            const vals = items.filter(i => i[field] != null).map(i => i[field]);
            return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
          };
          const avgFloat = (field) => {
            const vals = items.filter(i => i[field] != null).map(i => i[field]);
            return vals.length ? parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)) : null;
          };
          return {
            date: periodStart,
            steps: avg('steps'),
            distance: avgFloat('distance'),
            calories: avg('calories'),
            minutes_very_active: avg('minutes_very_active'),
          };
        });
    }

    const windowSize = agg === 'daily' ? 7 : agg === 'weekly' ? 4 : 3;
    const result = processed.map((d, i) => {
      const window = processed.slice(Math.max(0, i - windowSize + 1), i + 1);
      const avgVals = window.filter(w => w.steps != null).map(w => w.steps);
      const movingAvg = avgVals.length >= 2
        ? Math.round(avgVals.reduce((a, b) => a + b, 0) / avgVals.length)
        : null;

      return {
        date: d.date,
        steps: d.steps || 0,
        distance: d.distance || 0,
        calories: d.calories || 0,
        activeMinutes: d.minutes_very_active || 0,
        movingAvg,
      };
    });

    return { chartData: result, aggregation: agg };
  }, [filteredData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fg"></div>
      </div>
    );
  }

  if (!allData.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-fg-2">
            No activity data available. Sync your Fitbit to see activity data.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    if (aggregation === 'monthly') {
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
    const days = spanDays;
    if (days && days <= 14) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    if (days && days <= 90) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(chartData.length / 8) - 1);

  const StepsTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = aggregation === 'monthly'
      ? new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : aggregation === 'weekly'
        ? `Week of ${new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    return (
      <div className="bg-raised border border-line rounded-lg p-3 text-sm">
        <p className="font-medium text-fg mb-2">{dateLabel}</p>
        {aggregation !== 'daily' && (
          <p className="text-sm text-fg-3 mb-2">({aggregation} average)</p>
        )}
        <div className="space-y-1">
          <p style={{ color: COLORS.steps }}>
            Steps: <span className="font-medium">{d.steps?.toLocaleString()}</span>
          </p>
          {d.movingAvg != null && (
            <p style={{ color: COLORS.movingAvg }}>
              7-day Avg: <span className="font-medium">{d.movingAvg?.toLocaleString()}</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  const SimpleTooltip = ({ active, payload, label, color, valueKey, valueSuffix = '' }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const val = d[valueKey];
    const formatted = valueKey === 'distance' ? val?.toFixed(2) : val?.toLocaleString();
    return (
      <div className="bg-raised border border-line rounded-lg p-3 text-sm">
        <p className="font-medium text-fg mb-1">{dateLabel}</p>
        <p style={{ color }}>
          {valueSuffix ? `${formatted} ${valueSuffix}` : formatted}
        </p>
      </div>
    );
  };

  const latest = stats?.latest;
  const singleDay = spanDays === 1;

  return (
    <div className="space-y-6">
      {/* Range Selector */}
      <RangeSelector
        {...selectorProps}
        right={(
          <div className="flex shrink-0 items-center gap-3 text-sm text-fg-2">
            {aggregation !== 'daily' && (
              <span className="bg-elev px-2 py-1 rounded text-sm">
                {aggregation} averages
              </span>
            )}
            <span>{stats?.totalDays} days with data</span>
          </div>
        )}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Steps</p>
            <p className="text-2xl font-medium" style={{ color: COLORS.steps }}>
              {latest?.steps?.toLocaleString() ?? '--'}
            </p>
            {!singleDay && (
              <p className="text-sm text-fg-2 mt-1">
                Avg: {stats?.avgSteps?.toLocaleString() ?? '--'}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Distance</p>
            <p className="text-2xl font-medium" style={{ color: COLORS.distance }}>
              {latest?.distance != null ? `${latest.distance.toFixed(2)} km` : '--'}
            </p>
            {!singleDay && (
              <p className="text-sm text-fg-2 mt-1">
                Avg: {stats?.avgDistance != null ? `${stats.avgDistance.toFixed(2)} km` : '--'}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Calories</p>
            <p className="text-2xl font-medium" style={{ color: COLORS.calories }}>
              {latest?.calories?.toLocaleString() ?? '--'}
            </p>
            {!singleDay && (
              <p className="text-sm text-fg-2 mt-1">
                Avg: {stats?.avgCalories?.toLocaleString() ?? '--'}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Active Minutes</p>
            <p className="text-2xl font-medium" style={{ color: COLORS.activeMinutes }}>
              {latest?.minutes_very_active?.toLocaleString() ?? '--'}
            </p>
            {!singleDay && (
              <p className="text-sm text-fg-2 mt-1">
                Avg: {stats?.avgActiveMinutes?.toLocaleString() ?? '--'} min
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Chart: Steps with moving average */}
      <Card>
        <CardHeader>
          <CardTitle>Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                interval={tickInterval}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
              />
              <YAxis
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                tick={{ fontSize: 14, fill: '#9ca3af' }}
                axisLine={{ stroke: '#4b5563' }}
                tickLine={{ stroke: '#4b5563' }}
                width={48}
              />
              <Tooltip content={<StepsTooltip />} />
              <Legend
                formatter={(value) => value === 'movingAvg' ? '7-day Avg' : 'Steps'}
                wrapperStyle={{ fontSize: 14 }}
              />
              <ReferenceLine
                y={10000}
                stroke="#6b7280"
                strokeDasharray="3 3"
                label={{ value: '10k', position: 'insideTopLeft', fill: '#9ca3af', fontSize: 14 }}
              />
              <Bar dataKey="steps" fill={COLORS.steps} name="steps" radius={[2, 2, 0, 0]} />
              <Line
                dataKey="movingAvg"
                stroke={COLORS.movingAvg}
                strokeWidth={2}
                dot={false}
                name="movingAvg"
                type="monotone"
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Secondary Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Distance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Distance (km)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  interval={tickInterval}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                />
                <YAxis
                  tickFormatter={(v) => `${v}`}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                  width={35}
                />
                <Tooltip content={<SimpleTooltip color={COLORS.distance} valueKey="distance" valueSuffix="km" />} />
                <Bar dataKey="distance" fill={COLORS.distance} radius={[2, 2, 0, 0]} name="Distance" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Calories */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Calories</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  interval={tickInterval}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                />
                <YAxis
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                  width={35}
                />
                <Tooltip content={<SimpleTooltip color={COLORS.calories} valueKey="calories" />} />
                <Bar dataKey="calories" fill={COLORS.calories} radius={[2, 2, 0, 0]} name="Calories" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Active Minutes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Active Minutes</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  interval={tickInterval}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                />
                <YAxis
                  tickFormatter={(v) => `${v}m`}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                  width={35}
                />
                <Tooltip content={<SimpleTooltip color={COLORS.activeMinutes} valueKey="activeMinutes" valueSuffix="min" />} />
                <Bar dataKey="activeMinutes" fill={COLORS.activeMinutes} radius={[2, 2, 0, 0]} name="Active Minutes" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ActivityChart;
